import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";

function randInt(min, max, exclude = []) {
  let n;
  do {
    n = Math.floor(Math.random() * (max - min + 1)) + min;
  } while (exclude.includes(n));
  return n;
}

function explicitMultiply(expr) {
  let s = expr;
  s = s.replace(/(\d)(x)/g, "$1*$2");
  s = s.replace(/(\d)\(/g, "$1*(");
  s = s.replace(/\)(\d|x)/g, ")*$1");
  s = s.replace(/x\(/g, "x*(");
  return s;
}

function toLatexFractionLocal(raw) {
  if (typeof raw !== "string") return "";

  let s = raw.replace(/\s+/g, "");

  const terms = [];
  let current = "";
  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];
    const isSign = (ch === "+" || ch === "-") && i > 0 && s[i - 1] !== "^";
    if (isSign) {
      if (current) terms.push(current);
      current = ch;
    } else {
      current += ch;
    }
  }
  if (current) terms.push(current);
  const latexTerms = terms.map((term) => {
    const rationalPowerMatch = term.match(/^([+-]?\d+)\/x\^([+-]?\d+)$/);
    if (rationalPowerMatch) {
      const [, coef, power] = rationalPowerMatch;
      const abs = Math.abs(Number(coef));
      const sign = Number(coef) < 0 ? "-" : "";
      return `${sign}\\dfrac{${abs}}{x^{${power}}}`;
    }

    const rationalMatch = term.match(/^([+-]?\d+)\/x$/);
    if (rationalMatch) {
      const [, coef] = rationalMatch;
      const abs = Math.abs(Number(coef));
      const sign = Number(coef) < 0 ? "-" : "";
      return `${sign}\\dfrac{${abs}}{x}`;
    }

    return term
      .replace(/x\^(-\d+)/g, "x^{$1}")
      .replace(/\*/g, "\\cdot ");
  });

  return latexTerms
    .map((term, idx) => {
      if (idx === 0) return term;
      return term.startsWith("-") ? ` - ${term.slice(1)}` : ` + ${term}`;
    })
    .join("");
}

function normalizeExpression(raw) {
  let s = typeof raw === "string" ? raw : "";
  s = s.replace(/^\s*f'?\(x\)\s*=\s*/i, "");
  s = s.replace(/^\s*y\s*=\s*/i, "");
  s = s.replace(/\u2212/g, "-");
  s = s.replace(/(\d),(\d)/g, "$1.$2");
  s = s.replace(/\s+/g, "");
  s = explicitMultiply(s);
  s = s.replace(/\^/g, "**");
  return s;
}

function safeEval(expr, x) {
  const normalized = normalizeExpression(expr);
  if (!normalized) return NaN;
  const js = normalized.replace(/x/g, `(${x})`);
  try {
    const value = Function(`"use strict"; return (${js});`)();
    return Number.isFinite(value) ? value : NaN;
  } catch {
    return NaN;
  }
}

function areEquivalent(userExpr, expectedExpr) {
  const points = [-3, -2, -1, 1, 2, 3];
  for (const x of points) {
    const userVal = safeEval(userExpr, x);
    const expectedVal = safeEval(expectedExpr, x);
    if (!Number.isFinite(userVal) || !Number.isFinite(expectedVal)) return false;
    if (Math.abs(userVal - expectedVal) > 0.001) return false;
  }
  return true;
}

function latexPower(power) {
  return power === 1 ? "x" : `x^{${power}}`;
}

function formatPositiveTermLatex(coef, power, isFirst = false) {
  const sign = coef < 0 ? "-" : "+";
  const abs = Math.abs(coef);
  const prefix = isFirst ? (coef < 0 ? "-" : "") : ` ${sign} `;

  if (power === 0) return `${prefix}${abs}`;
  if (power === 1) return `${prefix}${abs === 1 ? "x" : `${abs}x`}`;
  return `${prefix}${abs === 1 ? latexPower(power) : `${abs}${latexPower(power)}`}`;
}

function formatRationalTermLatex(coef, denomPower, isFirst = false) {
  const sign = coef < 0 ? "-" : "+";
  const abs = Math.abs(coef);
  const prefix = isFirst ? (coef < 0 ? "-" : "") : ` ${sign} `;
  const denominator = denomPower === 1 ? "x" : `x^{${denomPower}}`;
  return `${prefix}\\dfrac{${abs}}{${denominator}}`;
}

function formatTermLatex(term, isFirst = false) {
  if (term.power >= 0) return formatPositiveTermLatex(term.coef, term.power, isFirst);
  return formatRationalTermLatex(term.coef, Math.abs(term.power), isFirst);
}

function expressionLatex(terms) {
  const nonZero = terms.filter((t) => t.coef !== 0);
  if (!nonZero.length) return "0";
  return nonZero.map((t, idx) => formatTermLatex(t, idx === 0)).join("");
}

function expressionAscii(terms) {
  const nonZero = terms.filter((t) => t.coef !== 0);
  if (!nonZero.length) return "0";

  return nonZero
    .map((t, idx) => {
      const sign = t.coef < 0 ? "-" : "+";
      const abs = Math.abs(t.coef);
      const prefix = idx === 0 ? (t.coef < 0 ? "-" : "") : ` ${sign} `;

      if (t.power === 0) return `${prefix}${abs}`;
      if (t.power === 1) return `${prefix}${abs === 1 ? "x" : `${abs}x`}`;
      return `${prefix}${abs === 1 ? `x^${t.power}` : `${abs}x^${t.power}`}`;
    })
    .join("");
}

function derivativeTerms(terms) {
  return terms
    .filter((t) => t.power !== 0)
    .map((t) => ({ coef: t.coef * t.power, power: t.power - 1 }))
    .filter((t) => t.coef !== 0);
}

function buildTaskTerms() {
  const terms = [];

  const positiveCount = randInt(2, 3);
  const usedPositivePowers = new Set();
  while (terms.length < positiveCount) {
    const power = randInt(0, 3);
    if (usedPositivePowers.has(power)) continue;
    usedPositivePowers.add(power);
    terms.push({ coef: randInt(-6, 6, [0]), power });
  }

  const rationalCount = randInt(1, 2);
  const usedNegativePowers = new Set();
  while (usedNegativePowers.size < rationalCount) {
    const denomPower = randInt(1, 3);
    if (usedNegativePowers.has(denomPower)) continue;
    usedNegativePowers.add(denomPower);
    terms.push({ coef: randInt(-7, 7, [0]), power: -denomPower });
  }

  return terms.sort((a, b) => b.power - a.power);
}

export function generateTask() {
  const terms = buildTaskTerms();
  const derivative = derivativeTerms(terms);
  const functionLatex = expressionLatex(terms);
  const derivativeLatex = expressionLatex(derivative);
  const derivativeAscii = expressionAscii(derivative);

  return {
    type: "RationalPolynomialDerivativeTask",
    prompt: `Oblicz pochodna funkcji: f(x) = ${functionLatex}. Podaj f'(x).`,
    answer: derivativeAscii,
    data: {
      terms,
      derivative,
      functionLatex,
      derivativeLatex,
      derivativeAscii,
    },
  };
}

export function renderTask({ data }) {
  return (
    <div className="space-y-2">
      <p>Oblicz pochodna funkcji:</p>
      <BlockMath math={`f(x) = ${data.functionLatex}`} />
      <p>Podaj <InlineMath math={"f'(x)"} />.</p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const hasPreview = typeof userAnswer === "string" && userAnswer.trim() !== "";

  return (
    <div className="flex flex-col gap-2">
      <div><InlineMath math={"f'(x) ="} /></div>
      <EquationInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        placeholder="np. 6x-2-3x^-2 albo 6x-2-3/x^2"
        basis="clamp(220px, 34ch, 340px)"
        showPreview={false}
      />
      {hasPreview ? (
        <div>
          <strong>Podgląd:</strong>{" "}
          <InlineMath math={`f'(x) = ${toLatexFractionLocal(userAnswer)}`} />
        </div>
      ) : null}
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (typeof userAnswer !== "string" || !userAnswer.trim()) return false;
  return areEquivalent(userAnswer, data.derivativeAscii);
}

export function renderUserAnswer(userAnswer) {
  if (typeof userAnswer !== "string" || !userAnswer.trim()) {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={`f'(x) = ${toLatexFractionLocal(userAnswer)}`} />;
}

export function renderAnswer({ data }) {
  return <InlineMath math={`f'(x) = ${data.derivativeLatex}`} />;
}

export function renderExplanation({ data }) {
  const positiveSteps = data.terms
    .filter((t) => t.power >= 0)
    .map((t) => {
      const original = formatTermLatex(t, true).replace(/^\+\s*/, "");
      if (t.power === 0) {
        return { original, derived: "0" };
      }
      const derived = formatTermLatex({ coef: t.coef * t.power, power: t.power - 1 }, true).replace(/^\+\s*/, "");
      return { original, derived };
    });

  const rationalSteps = data.terms
    .filter((t) => t.power < 0)
    .map((t) => {
      const original = formatTermLatex(t, true).replace(/^\+\s*/, "");
      const rewritten = formatPositiveTermLatex(t.coef, t.power, true).replace(/^\+\s*/, "");
      const intermediate = formatPositiveTermLatex(t.coef * t.power, t.power - 1, true).replace(/^\+\s*/, "");
      const derived = formatTermLatex({ coef: t.coef * t.power, power: t.power - 1 }, true).replace(/^\+\s*/, "");
      return { original, rewritten, intermediate, derived };
    });

  return (
    <div className="space-y-2" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1:</strong> Skladniki typu <InlineMath math={"\\dfrac{a}{x^n}"} /> zapisujemy jako{" "}
        <InlineMath math={"ax^{-n}"} />, a potem korzystamy z reguly <InlineMath math={"(ax^r)' = a \\cdot r x^{r-1}"} />.
      </p>
      <BlockMath math={`f(x) = ${data.functionLatex}`} />

      {rationalSteps.length > 0 && (
        <>
          <p><strong>Krok 2:</strong> Przepisujemy skladniki wymierne na potegi ujemne i rozniczkujemy:</p>
          {rationalSteps.map((step, idx) => (
            <BlockMath key={`r-${idx}`} math={`${step.original} = ${step.rewritten} \\rightarrow ${step.intermediate} \\rightarrow ${step.derived}`} />
          ))}
        </>
      )}

      {positiveSteps.length > 0 && (
        <>
          <p><strong>Krok 3:</strong> Rozniczkujemy pozostale skladniki:</p>
          {positiveSteps.map((step, idx) => (
            <BlockMath key={`p-${idx}`} math={`${step.original} \\rightarrow ${step.derived}`} />
          ))}
        </>
      )}

      <p><strong>Krok 4:</strong> Zapisujemy wynik:</p>
      <BlockMath math={`f'(x) = ${data.derivativeLatex}`} />
    </div>
  );
}



